import { Suspense, lazy } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { Button, EmptyState, PageHeader, Spinner } from '@luacentrix/ui';
import { LEGACY_DASHBOARD_PATHS, ROUTES } from './app-nav';
import { AppShell } from './layouts/AppShell';
import { BlockedPage } from './pages/BlockedPage';

const LandingPage = lazy(() =>
  import('./pages/LandingPage').then(module => ({ default: module.LandingPage })),
);
const DocsPage = lazy(() =>
  import('./pages/DocsPage').then(module => ({ default: module.DocsPage })),
);
const TermsPage = lazy(() =>
  import('./pages/TermsPage').then(module => ({ default: module.TermsPage })),
);
const PrivacyPage = lazy(() =>
  import('./pages/PrivacyPage').then(module => ({ default: module.PrivacyPage })),
);
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then(module => ({ default: module.ContactPage })),
);
const TryObfuscatorPage = lazy(() =>
  import('./pages/TryObfuscatorPage').then(module => ({ default: module.TryObfuscatorPage })),
);
const OverviewPage = lazy(() =>
  import('./pages/OverviewPage').then(module => ({ default: module.OverviewPage })),
);
const ProjectsPage = lazy(() =>
  import('./pages/ProjectsPage').then(module => ({ default: module.ProjectsPage })),
);
const ScriptsPage = lazy(() =>
  import('./pages/ScriptsPage').then(module => ({ default: module.ScriptsPage })),
);
const ObfuscatorPage = lazy(() =>
  import('./pages/ObfuscatorPage').then(module => ({ default: module.ObfuscatorPage })),
);
const LicensesPage = lazy(() =>
  import('./pages/LicensesPage').then(module => ({ default: module.LicensesPage })),
);
const KeysystemPage = lazy(() =>
  import('./pages/KeysystemPage').then(module => ({ default: module.KeysystemPage })),
);
const SecurityPage = lazy(() =>
  import('./pages/SecurityPage').then(module => ({ default: module.SecurityPage })),
);
const StatusPage = lazy(() =>
  import('./pages/StatusPage').then(module => ({ default: module.StatusPage })),
);

function RouteFallback() {
  return (
    <div className="page-stack" role="status">
      <Spinner size="lg" />
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="page-stack">
      <PageHeader title="Page not found" description="No route is registered for this address." />
      <EmptyState
        icon="search"
        title="That page does not exist"
        description={`Registered pages: ${ROUTES.map(route => route.href).join(', ')}.`}
        action={
          <Link to="/">
            <Button variant="primary">Back to overview</Button>
          </Link>
        }
      />
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/blocked" element={<BlockedPage />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/try" element={<TryObfuscatorPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        {LEGACY_DASHBOARD_PATHS.map(entry => (
          <Route
            element={<Navigate replace to={entry.to} />}
            key={entry.from}
            path={entry.from.slice(1)}
          />
        ))}
        <Route element={<AppShell />} path="/app">
          <Route index element={<OverviewPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="scripts" element={<ScriptsPage />} />
          <Route path="obfuscator" element={<ObfuscatorPage />} />
          <Route path="keysystem" element={<KeysystemPage />} />
          <Route path="licenses" element={<LicensesPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route element={<NotFoundPage />} path="*" />
      </Routes>
    </Suspense>
  );
}
